import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Layout from 'antd/lib/layout/layout';
import Tabs from 'antd/lib/tabs';
import Text from 'antd/lib/typography/Text';
import Button from 'antd/lib/button';
import Alert from 'antd/lib/alert';
import Flex from 'antd/lib/flex';
import Divider from 'antd/lib/divider';
import Input from 'antd/lib/input';
import Collapse from 'antd/lib/collapse';
import { CaretRightOutlined } from '@ant-design/icons';
import {
    DashboardOutlined,
    UnorderedListOutlined,
    ArrowLeftOutlined,
    CheckCircleFilled,
    ClockCircleFilled,
    FieldTimeOutlined,
    ExclamationCircleFilled,
} from '@ant-design/icons';

import { RootState } from '../../reducers/reducers';
import {
    selectIsStepTimelineDrawerVisible,
    selectReleaseVersion,
    selectShouldShowAutomationsTimelineEventsOnly,
    selectStepArchetypeGroupByGroupIndexAndArchetypeGroupIndex,
    selectStepRun,
    selectStepRunsByGroupIndexAndArchetypeGroupIndex,
    selectTimelineEventsFiltersMap,
} from '@/selectors/selectors';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { displayTextForReleaseStepType } from '@/helpers/displayHelpers';
import { flatMap } from 'lodash';
import { ReleaseStepStatus } from '@/types';
import { StepRun } from '@/types/stepRun';
import { StepRunPanel } from './StepRunPanel';
import { setEventsTimelineDrawerVisibility } from '@/actions/ui';
import { updateTimelineEventsFilters } from '@/actions/filters';
import { EventsTimelineDrawer } from '../timelineEvents/EventsTimelineDrawer';
import { useTimelineEventsPagination } from '@/utils/hooks/useTimelineEventsPagination';
import { globalMessage } from '@/utils/globalMessage';
import { filterTimelineEvent } from '@/types/timelineEventsFilters';

export interface StepRunsSummaryProps {
    appId: string;
    releaseId: string;
    orgId: string;
    groupIndex: number;
    archetypeGroupIndex: number;
}

enum StepRunsSummaryTab {
    Summary = 'summary',
    Checklist = 'checklist',
    Notifications = 'notifications',
}

interface StepRunStatusAlertProps {
    stepRuns: StepRun[];
}

interface AlertConfig {
    message: string;
    description: string;
    icon: React.ReactNode;
    color: string;
    type: 'info' | 'warning' | 'error' | 'success';
}

interface StepStatusSectionProps {
    title: string;
    count: number;
    steps: StepRun[];
    color: string;
    defaultExpanded: boolean;
    orgId: string;
    appId: string;
    releaseId: string;
}

const StepStatusSection: React.FC<StepStatusSectionProps> = ({
    title,
    count,
    steps,
    color,
    defaultExpanded,
    orgId,
    appId,
    releaseId,
}) => {
    return (
        <Collapse
            defaultActiveKey={defaultExpanded ? ['1'] : []}
            ghost
            expandIcon={({ isActive }) => (
                <CaretRightOutlined
                    style={{
                        color: antGrey.grey6,
                        fontSize: 24,
                    }}
                    rotate={isActive ? 90 : 0}
                />
            )}
            style={{
                backgroundColor: 'white',
                border: 'none',
                margin: 18,
                marginTop: 0,
                padding: 0,
            }}
            items={[
                {
                    key: '1',
                    label: (
                        <Flex align="center" gap={12}>
                            <div
                                style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: color,
                                }}
                            />
                            <Text strong style={{ fontSize: 16, color: color }}>
                                {title}
                            </Text>
                            <Divider type="vertical" style={{ backgroundColor: antGrey.grey5, margin: 0 }} />
                            <Text style={{ color: antGrey.grey6 }}>{count}</Text>
                        </Flex>
                    ),
                    children: (
                        <div style={{ paddingLeft: 20 }}>
                            {steps.map((step) => (
                                <StepRunPanel
                                    orgId={orgId}
                                    appId={appId}
                                    releaseId={releaseId}
                                    key={step.id}
                                    stepRun={step}
                                />
                            ))}
                        </div>
                    ),
                    style: {
                        borderBottom: 'none',
                    },
                },
            ]}
        />
    );
};

const StepRunStatusAlert: React.FC<StepRunStatusAlertProps> = ({ stepRuns }) => {
    const hasBlockedSteps = stepRuns.some((stepRun) => stepRun.status === ReleaseStepStatus.Blocked);
    const hasInProgressSteps = stepRuns.some(
        (stepRun) =>
            stepRun.status === ReleaseStepStatus.Pending || stepRun.status === ReleaseStepStatus.PendingChecklistOnly,
    );
    const allReady = stepRuns.every((stepRun) => stepRun.status === ReleaseStepStatus.Ready);
    const allInactive = stepRuns.every((stepRun) => stepRun.status === ReleaseStepStatus.Inactive);

    let alertConfig: AlertConfig;

    const firstStepRun = stepRuns[0];

    if (hasBlockedSteps) {
        alertConfig = {
            message: 'Blocked',
            description: 'One or more step configs are blocked.',
            icon: <ExclamationCircleFilled />,
            color: runwayColors.statusRed,
            type: 'error',
        };
    } else if (hasInProgressSteps) {
        alertConfig = {
            message: 'In progress',
            description: 'One or more step configs are in progress.',
            icon: <ClockCircleFilled />,
            color: runwayColors.statusYellow,
            type: 'warning',
        };
    } else if (allReady) {
        const description = firstStepRun.statusReasonString ?? 'All steps are ready!';
        alertConfig = {
            message: 'Ready',
            description: description,
            icon: <CheckCircleFilled />,
            color: runwayColors.statusGreen,
            type: 'success',
        };
    } else if (allInactive) {
        const description = firstStepRun.statusReasonString ?? 'Waiting for Kickoff to be ready.';
        alertConfig = {
            message: 'Inactive',
            description: description,
            icon: <ClockCircleFilled />,
            color: antGrey.grey6,
            type: 'info',
        };
    } else {
        alertConfig = {
            message: 'Unknown',
            description: 'Status unknown.',
            icon: <ClockCircleFilled />,
            color: antGrey.grey6,
            type: 'info',
        };
    }

    return (
        <Alert
            message={
                <Flex align="center" gap={8}>
                    <Text strong style={{ color: alertConfig.color }}>
                        {alertConfig.message} /{' '}
                    </Text>
                    <Text style={{ color: antGrey.grey7 }}>{alertConfig.description}</Text>
                </Flex>
            }
            type={alertConfig.type}
            showIcon
            icon={<span style={{ color: alertConfig.color }}>{alertConfig.icon}</span>}
            style={{
                margin: 36,
            }}
        />
    );
};

interface StepStatusNavigationProps {
    inactiveSteps: StepRun[];
    readySteps: StepRun[];
    pendingSteps: StepRun[];
    blockedSteps: StepRun[];
    inactiveRef: React.RefObject<HTMLDivElement>;
    readyRef: React.RefObject<HTMLDivElement>;
    pendingRef: React.RefObject<HTMLDivElement>;
    blockedRef: React.RefObject<HTMLDivElement>;
}

const StepStatusNavigation: React.FC<StepStatusNavigationProps> = ({
    inactiveSteps,
    readySteps,
    pendingSteps,
    blockedSteps,
    inactiveRef,
    readyRef,
    pendingRef,
    blockedRef,
}) => {
    const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
        ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const statusConfig = [
        { steps: inactiveSteps, ref: inactiveRef, color: antGrey.grey6, label: 'inactive' },
        { steps: readySteps, ref: readyRef, color: runwayColors.statusGreen, label: 'ready' },
        { steps: pendingSteps, ref: pendingRef, color: runwayColors.statusYellow, label: 'pending' },
        { steps: blockedSteps, ref: blockedRef, color: runwayColors.statusRed, label: 'blocked' },
    ];

    const visibleStatuses = statusConfig.filter((config) => config.steps.length > 0);

    return (
        <Flex align="center" gap={16} style={{ visibility: visibleStatuses.length > 0 ? 'visible' : 'hidden' }}>
            <Text style={{ fontSize: 14, color: antGrey.grey7 }}>Steps:</Text>
            <Flex gap={8} align="center">
                {visibleStatuses.map((config, index) => (
                    <React.Fragment key={config.label}>
                        <Button
                            type="link"
                            onClick={() => scrollToSection(config.ref)}
                            style={{ padding: 0, height: 'auto', color: config.color, textDecoration: 'underline' }}
                        >
                            {config.steps.length} {config.label}
                        </Button>
                        {index < visibleStatuses.length - 1 && (
                            <Divider type="vertical" style={{ backgroundColor: antGrey.grey5, margin: 0 }} />
                        )}
                    </React.Fragment>
                ))}
            </Flex>
        </Flex>
    );
};

export const StepRunsSummary: React.FC<StepRunsSummaryProps> = (props) => {
    const { releaseId, groupIndex, archetypeGroupIndex, orgId, appId } = props;

    const stepRunStatusData = useSelector((state: RootState) =>
        selectStepRunsByGroupIndexAndArchetypeGroupIndex(state, releaseId, groupIndex, archetypeGroupIndex),
    );

    const releaseVersion = useSelector((state: RootState) => selectReleaseVersion(state, releaseId));

    const stepRuns = useSelector(
        (state: RootState) =>
            stepRunStatusData?.map((stepRunStatusDatum) => selectStepRun(state, stepRunStatusDatum.stepRunId)) ?? [],
    );

    const archetypeGroup = useSelector((state: RootState) =>
        selectStepArchetypeGroupByGroupIndexAndArchetypeGroupIndex(state, releaseId, groupIndex, archetypeGroupIndex),
    );

    const [activeTab, setActiveTab] = React.useState(StepRunsSummaryTab.Summary);
    const [searchQuery, setSearchQuery] = React.useState('');

    const inactiveRef = React.useRef<HTMLDivElement>(null);
    const readyRef = React.useRef<HTMLDivElement>(null);
    const pendingRef = React.useRef<HTMLDivElement>(null);
    const blockedRef = React.useRef<HTMLDivElement>(null);

    const allPlatforms = flatMap(stepRuns, (stepRun) =>
        stepRun.destinations.map((destination) => destination.platform),
    );
    const allAppStoreTypes = flatMap(stepRuns, (stepRun) =>
        stepRun.destinations.map((destination) => destination.appStoreType),
    );

    const groupName =
        archetypeGroup?.name ||
        displayTextForReleaseStepType(archetypeGroup?.archetype, allPlatforms, allAppStoreTypes, true);

    const filteredStepRuns = React.useMemo(() => {
        if (!searchQuery.trim()) {
            return stepRuns;
        }

        const query = searchQuery.toLowerCase().trim();
        return stepRuns.filter((sr) => {
            const stepName = (
                sr.displayName ||
                displayTextForReleaseStepType(
                    sr.type,
                    sr.destinations.map((d) => d.platform),
                    sr.destinations.map((d) => d.appStoreType),
                    true,
                )
            ).toLowerCase();
            const stepId = sr.id.toLowerCase();
            return stepName.includes(query) || stepId.includes(query);
        });
    }, [stepRuns, searchQuery]);

    const inactiveSteps = filteredStepRuns.filter((sr) => sr.status === ReleaseStepStatus.Inactive);
    const readySteps = filteredStepRuns.filter((sr) => sr.status === ReleaseStepStatus.Ready);
    const pendingSteps = filteredStepRuns.filter(
        (sr) => sr.status === ReleaseStepStatus.Pending || sr.status === ReleaseStepStatus.PendingChecklistOnly,
    );
    const blockedSteps = filteredStepRuns.filter((sr) => sr.status === ReleaseStepStatus.Blocked);

    const dispatch = useDispatch();

    const stepRunDisplayName = React.useMemo(
        () =>
            stepRuns
                .map(
                    (sr) =>
                        sr.displayName ||
                        displayTextForReleaseStepType(
                            sr.type,
                            sr.destinations.map((d) => d.platform),
                            sr.destinations.map((d) => d.appStoreType),
                            true,
                        ),
                )
                .join(', '),
        [stepRuns],
    );

    const isDrawerVisible = useSelector((state: RootState) =>
        selectIsStepTimelineDrawerVisible(state, stepRunDisplayName),
    );

    const { events, isLoading, error, loadMore, isLoadingMore, paginationInfo } = useTimelineEventsPagination({
        orgId,
        appId,
        releaseId,
        stepRunIds: stepRuns.map((sr) => sr.id),
        initialLoad: true,
    });
    const currentFiltersMap = useSelector((state: RootState) => selectTimelineEventsFiltersMap(state));
    const shouldShowAutomationsOnly = useSelector((state: RootState) =>
        selectShouldShowAutomationsTimelineEventsOnly(state),
    );

    const filteredEvents = React.useMemo(
        () => events?.filter((event) => filterTimelineEvent(event, currentFiltersMap, shouldShowAutomationsOnly)) || [],
        [events, currentFiltersMap, shouldShowAutomationsOnly],
    );

    React.useEffect(() => {
        if (error) {
            globalMessage.error('An error occurred when fetching step run timeline events.');
        }
    }, [error]);

    const handleDrawerClose = React.useCallback(() => {
        dispatch(setEventsTimelineDrawerVisibility(null, false));
        dispatch(updateTimelineEventsFilters({ filters: [] }));
    }, [dispatch]);

    return (
        <>
            <Layout style={{ minWidth: 1000, overflowY: 'auto' }}>
                <StepRunStatusAlert stepRuns={stepRuns} />

                <div style={{ paddingLeft: 36, paddingRight: 36 }}>
                    <Flex align="center" gap={8}>
                        <Text
                            style={{
                                fontSize: 18,
                                fontWeight: 600,
                                letterSpacing: '0.1em',
                                color: antGrey.grey7,
                                textTransform: 'uppercase',
                                marginRight: 36,
                            }}
                        >
                            {groupName}
                        </Text>
                        <Tabs
                            activeKey={activeTab}
                            onChange={(activeKey) => setActiveTab(activeKey as StepRunsSummaryTab)}
                            type="card"
                            tabBarStyle={{ marginBottom: 0 }}
                            items={[
                                {
                                    key: StepRunsSummaryTab.Summary,
                                    label: (
                                        <Flex align="center" gap={4}>
                                            <DashboardOutlined
                                                style={{
                                                    color:
                                                        activeTab === StepRunsSummaryTab.Summary ?
                                                            runwayColors.info
                                                        :   antGrey.grey6,
                                                }}
                                            />
                                            <Text
                                                style={{
                                                    color:
                                                        activeTab === StepRunsSummaryTab.Summary ?
                                                            antGrey.grey8
                                                        :   antGrey.grey7,
                                                }}
                                            >
                                                Summary
                                            </Text>
                                        </Flex>
                                    ),
                                },
                                {
                                    key: StepRunsSummaryTab.Checklist,
                                    label: (
                                        <Flex align="center" gap={4}>
                                            <UnorderedListOutlined
                                                style={{
                                                    color:
                                                        activeTab === StepRunsSummaryTab.Checklist ?
                                                            runwayColors.info
                                                        :   antGrey.grey6,
                                                }}
                                            />
                                            <Text
                                                style={{
                                                    color:
                                                        activeTab === StepRunsSummaryTab.Checklist ?
                                                            antGrey.grey8
                                                        :   antGrey.grey7,
                                                }}
                                            >
                                                Checklist
                                            </Text>
                                            <Text style={{ color: runwayColors.statusGreen }}>4 of 4</Text>
                                            <CheckCircleFilled style={{ color: runwayColors.statusGreen }} />
                                        </Flex>
                                    ),
                                },
                                //TODO: hidden for  now
                                // {
                                //     key: StepRunsSummaryTab.Notifications,
                                //     label: (
                                //         <Flex align="center" gap={4}>
                                //             <BellOutlined
                                //                 style={{
                                //                     color:
                                //                         activeTab === StepRunsSummaryTab.Notifications ?
                                //                             runwayColors.info
                                //                         :   antGrey.grey6,
                                //                 }}
                                //             />
                                //             <Text
                                //                 style={{
                                //                     color:
                                //                         activeTab === StepRunsSummaryTab.Notifications ?
                                //                             antGrey.grey8
                                //                         :   antGrey.grey7,
                                //                 }}
                                //             >
                                //                 Notifications
                                //             </Text>
                                //         </Flex>
                                //     ),
                                // },
                            ]}
                        />
                        <Flex align="center" gap={8}>
                            <FieldTimeOutlined style={{ color: antGrey.grey7 }} />
                            <Text
                                style={{
                                    fontSize: '13px',
                                    color: antGrey.grey7,
                                    letterSpacing: '0.1em',
                                    textTransform: 'uppercase',
                                    fontWeight: 400,
                                }}
                            >
                                TIMELINE
                            </Text>
                            <Divider type="vertical" style={{ backgroundColor: antGrey.grey5 }} />
                            <Button
                                type="link"
                                style={{ padding: 0, fontSize: '13px' }}
                                onClick={() => {
                                    dispatch(setEventsTimelineDrawerVisibility(stepRunDisplayName, true));
                                }}
                            >
                                View
                                <ArrowLeftOutlined />
                            </Button>
                        </Flex>
                    </Flex>
                </div>

                <div style={{ padding: '16px 36px', paddingTop: 36 }}>
                    <Flex align="center" justify="space-between">
                        <StepStatusNavigation
                            inactiveSteps={inactiveSteps}
                            readySteps={readySteps}
                            pendingSteps={pendingSteps}
                            blockedSteps={blockedSteps}
                            inactiveRef={inactiveRef}
                            readyRef={readyRef}
                            pendingRef={pendingRef}
                            blockedRef={blockedRef}
                        />
                        <Input.Search
                            placeholder="Search by name or ID"
                            style={{ width: 240 }}
                            allowClear
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </Flex>
                </div>

                <Divider style={{ margin: 0, marginBottom: 18 }} />
                <div>
                    {blockedSteps.length > 0 && (
                        <div ref={blockedRef}>
                            <StepStatusSection
                                title="Blocked"
                                count={blockedSteps.length}
                                steps={blockedSteps}
                                color={runwayColors.statusRed}
                                defaultExpanded={true}
                                orgId={orgId}
                                appId={appId}
                                releaseId={releaseId}
                            />
                        </div>
                    )}
                    {pendingSteps.length > 0 && (
                        <div ref={pendingRef}>
                            <StepStatusSection
                                title="Pending"
                                count={pendingSteps.length}
                                steps={pendingSteps}
                                color={runwayColors.statusYellow}
                                defaultExpanded={blockedSteps.length === 0}
                                orgId={orgId}
                                appId={appId}
                                releaseId={releaseId}
                            />
                        </div>
                    )}
                    {readySteps.length > 0 && (
                        <div ref={readyRef}>
                            <StepStatusSection
                                title="Ready"
                                count={readySteps.length}
                                steps={readySteps}
                                color={runwayColors.statusGreen}
                                defaultExpanded={blockedSteps.length === 0 && pendingSteps.length === 0}
                                orgId={orgId}
                                appId={appId}
                                releaseId={releaseId}
                            />
                        </div>
                    )}
                    {inactiveSteps.length > 0 && (
                        <div ref={inactiveRef}>
                            <StepStatusSection
                                title="Inactive"
                                count={inactiveSteps.length}
                                steps={inactiveSteps}
                                color={antGrey.grey6}
                                defaultExpanded={false}
                                orgId={orgId}
                                appId={appId}
                                releaseId={releaseId}
                            />
                        </div>
                    )}
                </div>
            </Layout>
            <EventsTimelineDrawer
                events={filteredEvents}
                open={isDrawerVisible}
                onClose={handleDrawerClose}
                orgId={orgId}
                appId={appId}
                version={releaseVersion}
                releaseStepDisplayName={stepRunDisplayName}
                loadMore={loadMore}
                isLoading={isLoadingMore || isLoading}
                hasMore={paginationInfo?.hasMore}
            />
        </>
    );
};
